import React, { PropTypes } from 'react';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { routerShape } from 'react-router';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { getLabel } from '@bibliocommons/utils-filter';
import * as bcPropTypes from '@bibliocommons/bc-prop-types';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import Checkbox from '@bibliocommons/base-checkbox';
import { Arrows } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import ExpandFilterLink from '../ExpandFilterLink';
import HelpLinkContainer from '../../../../containers/HelpLinkContainer';
import formatFilterForQuery from '../../../../helpers/facets/formatFilterForQuery';
import formatFiltersForQuery from '../../../../helpers/facets/formatFiltersForQuery';

import './LanguageField.scss';

class LanguageField extends BcReactComponent {
  constructor(props) {
    super(props);
    this.state = {
      translationsIncluded: false,
      isExpanded: false
    };
    this.bindAll('renderFilters', 'renderFilter', 'toggleTranslationsIncluded', 'expandField', 'handleToggleFilter', 'handleLanguageToggle');
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isExpanded && this.state.isExpanded && this._fieldToggle && this._fieldToggle._focusableElement) {
      this._fieldToggle._focusableElement.focus();
    }
  }

  handleLanguageToggle(fieldId) {
    const { router, activeFilters, transformQuery } = this.props;
    const { location } = router;

    const filters = activeFilters.get('LANGUAGE') || activeFilters.get('PRIMARY_LANGUAGE');

    if (filters) {
      let query = {
        ...location.query,
        ...formatFiltersForQuery(activeFilters),
        f_LANGUAGE: undefined,
        f_PRIMARY_LANGUAGE: undefined,
        ...formatFilterForQuery(fieldId, filters)
      };

      if (transformQuery) {
        query = transformQuery(query);
      }

      router.push({ ...location, query });
    }
  }

  toggleTranslationsIncluded() {
    const newToggleState = !this.state.translationsIncluded;
    this.setState({
      ...this.state,
      translationsIncluded: newToggleState
    });
    const fieldId = newToggleState ? 'LANGUAGE' : 'PRIMARY_LANGUAGE';

    return this.handleLanguageToggle(fieldId);
  }

  handleToggleFilter(checked, { value }) {
    this.props.handleToggleFilter(this.getCurrentFieldId(), value);
  }

  getCurrentFieldId() {
    return this.state.translationsIncluded && this.props.language ? this.props.language.get('id') : this.props.primaryLanguage.get('id');
  }

  expandField() {
    this.setState({
      ...this.state,
      isExpanded: !this.state.isExpanded
    });
  }

  renderExpandIcon() {
    if (this.state.isExpanded) {
      return <Arrows.medium.Up color="#333" className="small" />;
    }

    return (<Arrows.medium.Down color="#333" className="small" />);
  }

  renderFilter(filter) {
    const value = filter.get('value');
    const label = filter.get('label');
    const currentFieldId = this.getCurrentFieldId();
    const renderKey = `${currentFieldId}-${value}`;
    if (!this.state.isExpanded) {
      return <span key={renderKey} />;
    }

    let classString = 'filter';
    if (filter.get('applied')) {
      classString += ' applied';
    }
    const filterLabel = getLabel(this.props.intl, 'LANGUAGE', label || value);
    const formattedLabel = this.props.intl.formatMessage({ id: 'Facets.filterLabelFormat' }, { value: filterLabel, count: filter.get('count') });

    return (
      <li key={renderKey} className={classString}>
        <Checkbox
          id={`field-${value}`}
          value={filter}
          handleChange={this.handleToggleFilter}
          checked={filter.get('applied')}
          label={formattedLabel} />
      </li>
    );
  }

  renderFilters() {
    const currentField = this.state.translationsIncluded ? this.props.language : this.props.primaryLanguage;
    const visibleFilter = (f, index) => index < 15 || f.get('applied');
    return currentField.get('fieldFilters').filter(visibleFilter).map(this.renderFilter);
  }

  renderFieldToggle() {
    if (!this.state.isExpanded) {
      return <span />;
    }
    return (
      <li key="language-toggle-checkbox" className="language-toggle">
        <Checkbox
          id="language-translation-toggle"
          checked={this.state.translationsIncluded}
          handleChange={this.toggleTranslationsIncluded}
          inline
          ref={(el) => { this._fieldToggle = el; }}
          label={this.props.intl.formatMessage({ id: 'include_translations' })} />
        <HelpLinkContainer
          className="small"
          page="045faq/030faq-searching/#trans">
          <ScreenReaderMessage>
            <FormattedMessage id="ada_include_translations_help_link" />
          </ScreenReaderMessage>
        </HelpLinkContainer>
        <hr />
      </li>
    );
  }

  renderExpandFilterLink() {
    const currentField = this.state.translationsIncluded ? this.props.language : this.props.primaryLanguage;
    if (currentField.get('hasMore') && this.state.isExpanded) {
      return (
        <ExpandFilterLink
          fieldId={currentField.get('id')}
          filters={currentField.get('fieldFilters')}
          label="LANGUAGE.field"
          handleToggleExpandedField={this.props.handleToggleExpandedField}
          handleCloseExpandField={this.props.handleCloseExpandField}
          handleExpandField={this.props.handleExpandField} />
      );
    }
    return null;
  }

  render() {
    if (this.props.primaryLanguage ? !this.props.language : this.props.language) {
      return null;
    }
    if (this.props.primaryLanguage || this.props.language) {
      return (
        <section className="cp-language-field">
          <a href="#expand-filter" onClick={this.expandField} className="field-toggle">
            {this.props.iconName || this.props.icon ? this.renderIcon() : null}
            <span className="field-text field-name">
              <FormattedMessage id="LANGUAGES.field" />
            </span>
            {this.renderExpandIcon()}
          </a>
          <ul className="filter-list">
            {this.renderFieldToggle()}
            {this.renderFilters()}
          </ul>
          {this.renderExpandFilterLink()}
        </section>
      );
    }
    return (<noop />);
  }
}

LanguageField.displayName = 'LanguageField';

LanguageField.propTypes = {
  primaryLanguage: bcPropTypes.searchFieldShape,
  language: bcPropTypes.searchFieldShape,
  activeFilters: ImmutablePropTypes.map,
  handleToggleFilter: PropTypes.func,
  transformQuery: PropTypes.func,
  intl: intlShape,
  key: PropTypes.string,
  router: routerShape
};

export default injectIntl(LanguageField);



// WEBPACK FOOTER //
// ./app/components/shared/Facets/LanguageField/LanguageField.jsx